import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PreLoader } from "../../subcomponents/Preloader";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  appendPayloadListItem,
  checkData,
  checkDuplicate,
  deleteItem,
  normalizePayloadList,
  updatePayloadListItem,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import InfoIcon from "@mui/icons-material/Info";
import { originsTooltip } from "../../../constants/TooltipInformation";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { RoomPropertyEnum } from "../../../constants/NavbarList";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const Origin = ({ data, fieldValues }: { data: any; fieldValues: any }) => {
  const [originCode, setOriginCode] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<boolean>(false);
  const [originName, setOriginName] = useState<string | null>(null);
  const [nameError, setNameError] = useState<boolean>(false);
  const [originInformation, setOriginInformation] = useState<any>(null);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [active, setActive] = useState<boolean>(true);
  const [routeLocalCalls, setRouteLocalCalls] = useState<boolean>(false);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const originPayload = {
    code: originCode,
    name: originName,
    isActive: active,
    isRouteLocalCalls: routeLocalCalls,
  };

  const addOrigin = () => {
    if (validateInput()) {
      if (!originCode || originCode === null || originCode === "") {
        setCodeError(true);
      }
      if (!originName || originName === null || originName === "") {
        setNameError(true);
      } else if (
        checkDuplicate(originInformation, DNDFieldsEnum.ORIGINCODE, originCode)
      ) {
        setAlertMessage("Cannot have duplicate entries");
        setAlertType("error");
        setOpenAlert(true);
      } else {
        setOriginInformation((prev: any) =>
          appendPayloadListItem(prev, originPayload),
        );
        setLastUpdatedRecord(originPayload);
        editOrigin(null);
        setEditClicked(false);
      }
    }
  };

  const editOrigin = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setOriginCode(list?.code);
    setOriginName(list?.name);
    setActive(list ? list.isActive : true);
    setRouteLocalCalls(list ? list.isRouteLocalCalls : false);
  };

  const updateOrigin = () => {
    if (!validateInput()) return false;
    if (
      checkDuplicate(
        (normalizePayloadList(originInformation) ?? []).filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.ORIGINCODE,
        originCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setNameError(false);
    setOriginInformation((prev: any) =>
      updatePayloadListItem(prev, selectedId, originPayload),
    );
    editOrigin(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${RoomPropertyEnum.origin} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const deleteOrigin = (id: number) => {
    setOriginInformation(deleteItem(originInformation, id));
  };

  const validateInput = () => {
    setCodeError(false);
    setNameError(false);
    let isValid = true;
    if (
      originCode &&
      !validateString(
        originCode,
        validationLength.originCode,
        validationType.CODE,
      )
    ) {
      setCodeError(true);
      isValid = false;
    }
    if (
      originName &&
      !validateString(
        originName,
        validationLength.originName,
        validationType.OTHER,
      )
    ) {
      setNameError(true);
      isValid = false;
    }
    return isValid;
  };

  useEffect(() => {
    data(originInformation);
    editClicked &&
      setSelectedId(
        checkData(originInformation, selectedId) ? selectedId : null,
      );
  }, [originInformation]);

  useEffect(() => {
    fieldValues && setOriginInformation(normalizePayloadList(fieldValues));
  }, []);

  useEffect(() => {
    validateInput();
  }, [originCode, originName]);

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Origin Information
              <Tooltip title={tooltipComponent(originsTooltip.header)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editOrigin(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editOrigin(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Stack
            className="form-two-col-row"
            direction="row"
            width="100%"
            gap="20px"
          >
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Origin Code
                <Tooltip title={tooltipComponent(originsTooltip.originCode)}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="originCode"
                variant="outlined"
                placeholder="Origin Code"
                helperText={
                  codeError
                    ? validationMessages.originCode
                    : originsTooltip.originCode
                }
                error={codeError}
                value={originCode ?? ""}
                sx={{ width: "100%" }}
                onChange={(e: any) => {
                  setOriginCode(e.target.value.toUpperCase());
                }}
              />
            </Stack>
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Origin Name
                <Tooltip title={tooltipComponent(originsTooltip.originName)}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="originName"
                variant="outlined"
                placeholder="Origin Name"
                helperText={
                  nameError
                    ? validationMessages.originName
                    : originsTooltip.originName
                }
                value={originName ?? ""}
                error={nameError}
                sx={{ width: "100%" }}
                onChange={(e: any) => {
                  setOriginName(e.target.value);
                }}
              />
            </Stack>
          </Stack>
          <Stack
            className="form-checkbox-row form-two-col-row"
            direction="row"
            gap="50px"
            width="100%"
          >
            <Stack direction="row" gap="2px">
              <FormControlLabel
                control={
                  <Switch
                    checked={active}
                    onChange={() => setActive(!active)}
                  />
                }
                label="Active?"
              />
              <Tooltip title={tooltipComponent(originsTooltip.active)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Stack direction="row" width="max-content" height="max-content">
              <FormControlLabel
                control={
                  <Checkbox
                    id="routeLocalCalls"
                    checked={routeLocalCalls}
                    onChange={(e: any) => setRouteLocalCalls(e.target.checked)}
                  />
                }
                label="Route local calls?"
              />
              <Tooltip title={tooltipComponent(originsTooltip.routeLocalCalls)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={selectedId ? updateOrigin : addOrigin}
              id="addNewOrigin"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Origin
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">Origin Available</Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {originInformation ? (
                <DraggableItemList
                  itemData={originInformation}
                  onOrderUpdate={(order) => setOriginInformation(order)}
                  onEdit={(info) => {
                    editOrigin(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteOrigin(id)}
                  code={DNDFieldsEnum.ORIGINCODE}
                  name={DNDFieldsEnum.ORIGINNAME}
                />
              ) : (
                <Typography variant="body2">
                  No Origin Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default Origin;
